import React from 'react';
import { X, Clock, CheckCircle2, CheckCircle, XCircle } from 'lucide-react';

interface Consumable {
    id: number;
    title: string;
}

interface PriceReductionRequest {
    id: number;
    consumable_id: number;
    consumable_title?: string;
    original_price: number;
    requested_price: number;
    requested_discount_percentage?: number | null;
    status: 'pending' | 'approved' | 'rejected';
    reason?: string;
    includes_tier_changes?: boolean;
    requested_tiers?: Array<{
        tier_number: number;
        min_quantity: number;
        free_units?: number;
        discount_percentage?: number;
    }> | null;
}

interface ReductionRequestsModalProps {
    isOpen: boolean;
    onClose: () => void;
    requests: PriceReductionRequest[];
    consumables: Consumable[];
    onApprove: (requestId: number) => void;
    onReject: (requestId: number) => void;
}

const ReductionRequestsModal: React.FC<ReductionRequestsModalProps> = ({
    isOpen,
    onClose,
    requests,
    consumables,
    onApprove,
    onReject
}) => {
    if (!isOpen) return null;

    const pendingCount = requests.filter(r => r.status === 'pending').length;

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-950/20 dark:to-amber-950/20">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-gradient-to-br from-orange-500 to-amber-600 rounded-lg">
                            <Clock className="h-5 w-5 text-white" />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">Demandes de réduction</h2>
                            <p className="text-sm text-slate-500 dark:text-slate-400">
                                {pendingCount} demande{pendingCount > 1 ? 's' : ''} en attente
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors">
                        <X className="h-5 w-5 text-slate-500" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-auto p-6">
                    {requests.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-64 text-slate-500 dark:text-slate-400">
                            <CheckCircle2 className="h-16 w-16 mb-4 opacity-50 text-green-500" />
                            <p className="text-lg font-medium">Aucune demande en attente</p>
                            <p className="text-sm mt-2">Toutes les demandes de réduction ont été traitées</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {requests.map((request) => {
                                const consumable = consumables.find(c => c.id === request.consumable_id);
                                const savingsPercent = request.requested_discount_percentage || 0;
                                const savingsAmount = request.original_price - request.requested_price;

                                return (
                                    <div
                                        key={request.id}
                                        className={`bg-white dark:bg-slate-800 border rounded-lg p-4 ${request.status === 'pending'
                                            ? 'border-orange-200 dark:border-orange-800'
                                            : request.status === 'approved'
                                                ? 'border-green-200 dark:border-green-800'
                                                : 'border-red-200 dark:border-red-800'
                                            }`}
                                    >
                                        <div className="flex items-start justify-between">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <h3 className="font-semibold text-slate-900 dark:text-slate-100">
                                                        {request.consumable_title || consumable?.title || `Produit #${request.consumable_id}`}
                                                    </h3>
                                                    <span
                                                        className={`px-2 py-1 text-xs rounded-full ${request.status === 'pending'
                                                            ? 'bg-orange-100 text-orange-700 dark:bg-orange-950/30 dark:text-orange-400'
                                                            : request.status === 'approved'
                                                                ? 'bg-green-100 text-green-700 dark:bg-green-950/30 dark:text-green-400'
                                                                : 'bg-red-100 text-red-700 dark:bg-red-950/30 dark:text-red-400'
                                                            }`}
                                                    >
                                                        {request.status === 'pending' ? 'En attente' : request.status === 'approved' ? 'Approuvé' : 'Rejeté'}
                                                    </span>
                                                </div>

                                                <div className="grid grid-cols-2 gap-4 text-sm mb-3">
                                                    <div>
                                                        <span className="text-slate-500 dark:text-slate-400">Prix original:</span>
                                                        <p className="font-medium text-slate-900 dark:text-slate-100">{request.original_price.toFixed(2)} €</p>
                                                    </div>
                                                    <div>
                                                        <span className="text-slate-500 dark:text-slate-400">Prix demandé:</span>
                                                        <p className="font-medium text-emerald-600 dark:text-emerald-400">{request.requested_price.toFixed(2)} €</p>
                                                    </div>
                                                    <div>
                                                        <span className="text-slate-500 dark:text-slate-400">Économie:</span>
                                                        <p className="font-medium text-orange-600 dark:text-orange-400">
                                                            {savingsAmount.toFixed(2)} € ({savingsPercent.toFixed(1)}%)
                                                        </p>
                                                    </div>
                                                </div>

                                                {request.reason && (
                                                    <div className="mb-3 p-3 bg-slate-50 dark:bg-slate-900/50 rounded-lg">
                                                        <span className="text-xs text-slate-500 dark:text-slate-400 font-medium">Raison:</span>
                                                        <p className="text-sm text-slate-700 dark:text-slate-300 mt-1">{request.reason}</p>
                                                    </div>
                                                )}

                                                {request.includes_tier_changes && request.requested_tiers && request.requested_tiers.length > 0 && (
                                                    <div className="mb-3 p-3 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                                                        <p className="text-sm text-blue-700 dark:text-blue-300 font-medium mb-2">
                                                            🎁 Configuration des paliers demandée
                                                        </p>
                                                        <div className="space-y-1">
                                                            {request.requested_tiers.map((tier, idx) => (
                                                                <div key={idx} className="text-xs text-blue-600 dark:text-blue-400">
                                                                    Palier {tier.tier_number}: {tier.min_quantity}+ unités
                                                                    {tier.free_units && ` → ${tier.free_units} gratuit${tier.free_units > 1 ? 's' : ''}`}
                                                                    {tier.discount_percentage && ` → ${tier.discount_percentage}%`}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            {request.status === 'pending' && (
                                                <div className="flex gap-2 ml-4">
                                                    <button
                                                        onClick={() => onApprove(request.id)}
                                                        className="flex items-center gap-1 px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors text-sm font-medium"
                                                    >
                                                        <CheckCircle className="h-4 w-4" />
                                                        Approuver
                                                    </button>
                                                    <button
                                                        onClick={() => onReject(request.id)}
                                                        className="flex items-center gap-1 px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors text-sm font-medium"
                                                    >
                                                        <XCircle className="h-4 w-4" />
                                                        Rejeter
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-700 flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg transition-colors"
                    >
                        Fermer
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ReductionRequestsModal;
